/**
 * „Neu in dieser Version" — what changed where people meet it, written by
 * hand with each release (Welle 1, idea 16). Curated, not generated: a
 * commit log speaks to the people who built it, this list to the people at
 * K60 who work with it. Newest first. German, factual, in the app's own
 * „du", no figures in per cent and no internal ids (CI §5, AN-14).
 *
 * The shell shows the newest entry once per person until it is put away;
 * the „?" overview keeps it readable afterwards.
 */
export interface WhatsNewEntry {
  /** Stable, never shown — what „put away" is remembered by. Never reuse one. */
  readonly id: string;
  /** One line for the note in the shell. */
  readonly lead: string;
  /** One or two sentences per change. */
  readonly items: readonly string[];
}

export const WHATS_NEW: readonly WhatsNewEntry[] = [
  {
    id: "2026-10-23",
    lead: "Start und Vorgänge stehen oben, die Schritte eines Vorgangs als Reiter.",
    items: [
      "Die Leiste links ist weg. Start und Vorgänge stehen oben im Kopf, die Zahl wartender Übergaben steht an „Start“.",
      "Ein Vorgang zeigt seine Schritte als Reiter über der Seite — Akte, Auslese oder Umriss, Planung, Kalkulation. Es steht nur da, was für diesen Vorgang gerade geht.",
      "Einen neuen Vorgang legst du auf der Startseite an: Zeichnung oder Mail einlesen, oder den Umriss von Hand zeichnen. Nach „Übernehmen“ geht es direkt in die Planung.",
      "Planung und Umriss sind breiter.",
      "Den Rosttyp eines Vorgangs wählst du am Eingang oder oben in der Planung; nennt die Mail einen, steht er als Vorschlag da.",
    ],
  },
  {
    id: "2026-10-18",
    lead: "Weniger Sackgassen, und die Taste „?“ zeigt die Tastenkürzel.",
    items: [
      "Planung eines Vorgangs: Der Knopf „Weiter zur Prüfung“ ist weg — er führte zu Beispielwerten. Weiter geht es mit „Weiter zur Kalkulation“.",
      "Anmeldung: Der Knopf für Microsoft erscheint erst, wenn die Anmeldung über Microsoft eingerichtet ist. Bis dahin meldest du dich mit E-Mail und Passwort an.",
      "Eine Adresse, die zu keinem Schirm führt, sagt das jetzt und bietet den Weg zur Startseite. Lässt sich ein Schirm nicht anzeigen, bleibt die Leiste links stehen.",
      "Kalkulation: Ändert sich der Preisstand bei einem freigegebenen Vorgang, nennt der Hinweis den Weg — erst die Freigabe zurückziehen, dann neu rechnen.",
      "Die Taste „?“ zeigt die Tastenkürzel des Schirms, auf dem du gerade bist. Auf einem breiten Bildschirm steht dafür oben rechts auch „Tastenkürzel“.",
      "Umriss und Planung: Neben „Rückgängig“ steht „Wiederholen“, und beides geht auch mit der Tastatur — Strg+Z nimmt den letzten Schritt zurück, Strg+Y setzt ihn wieder (am Mac ⌘Z und ⇧⌘Z). Zeigst du auf den Knopf, steht dort, welcher Schritt es ist.",
      "Umriss zeichnen: Der Entwurf bleibt in diesem Browser gesichert, bis du ihn übernimmst. Nach einem Neuladen bietet der Schirm an, ihn wiederherzustellen oder zu verwerfen.",
      "Planung: Oben steht, ob gespeichert ist — „Gespeichert“ mit dem Stand, „Speichert …“ oder „Nicht gespeichert“ mit dem Grund. Nach einer Belegung bietet die Meldung unten „Rückgängig“ an.",
      "Kalkulation: Unter jeder Position steht, woher ihr Preis kommt — bei Konsolen der Artikel aus dem Konsolenkatalog, bei der Fracht die K60-Frachtregel. Die Spalten heißen „EK Zeile“ und „VK Zeile“, und die interne Excel-Kalkulation hat eine Spalte „Preisherkunft“.",
      "Kalkulation: Die Freigabe steht als kurzer Vermerk da — wer, wann, Stand, Preisstand und die quittierten Lücken. Die gedruckten Unterlagen nennen davon nur, wer wann freigegeben hat und — bei einer Freigabe mit Lücken — wie viele es sind; Preisstand und die Namen der Lücken bleiben in der Kalkulation. Nach dem Zurückziehen steht dort, wer die Freigabe zurückgezogen hat, wann und warum.",
      "Ein wiederhergestellter Vorgang nennt im Kopf, wann und von wem er storniert und wiederhergestellt wurde, mit dem Grund.",
      "Einlesen: Der Schirm sagt, wie lange das Lesen meist dauert. Braucht die Anmeldung länger, steht „Sitzung wird geprüft …“ da statt einer leeren Seite.",
      "Seitenwechsel und die Schritte der Planung blenden ruhig über, und das Maschenfeld auf der Startseite steht still.",
    ],
  },
];

/** The entry the shell shows, if there is one. */
export function latestNews(): WhatsNewEntry | null {
  return WHATS_NEW[0] ?? null;
}

/** Per person: two people on one browser each see the note once. */
function storageKey(userId: string): string {
  return `k60.neu.${userId}`;
}

/**
 * The ids this person has put away. Storage is a convenience — blocked,
 * full, or holding something else, it reads as nothing seen, and the note
 * shows once more rather than the shell breaking.
 */
function seenIds(userId: string): string[] {
  try {
    const stored: unknown = JSON.parse(window.localStorage.getItem(storageKey(userId)) ?? "[]");
    return Array.isArray(stored) ? stored.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

export function newsSeen(userId: string, entryId: string): boolean {
  return seenIds(userId).includes(entryId);
}

export function markNewsSeen(userId: string, entryId: string): void {
  const ids = seenIds(userId);
  if (ids.includes(entryId)) return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify([...ids, entryId]));
  } catch {
    // Not remembered: the note is gone for this visit and shows once more on the next.
  }
}
